import { AxiosInstance } from 'axios'
import { CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react'
import { SourceType } from '@wove/api'

export const getStatusIcon = (status?: string) => {
  switch (status) {
    case 'success':
      return <CheckCircle className="h-4 w-4 text-green-500" />
    case 'error':
      return <AlertCircle className="h-4 w-4 text-red-500" />
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-amber-500" />
    default:
      return null
  }
}

function extractDisposition(responseHeaders: Record<string, unknown>): string {
  try {
    const filenamePattern = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const disposition = responseHeaders['content-disposition']
    const matches = filenamePattern.exec(typeof disposition === 'string' ? disposition : '')

    if (matches && matches[1]) {
      // Extracted filename
      const filename = matches[1].replace(/['"]/g, '') // Remove any surrounding quotes
      return filename
    } else {
      return 'data.xlsx'
    }
  } catch (error) {
    return 'data.xlsx'
  }
}

export const exportData = async (api: AxiosInstance, id: string, format: SourceType) => {
  try {
    const response = await api.get(`/api/v1/sources/${id}/export/${format}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'blob',
      withCredentials: true,
    })

    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', extractDisposition(response.headers))
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
    return true
  } catch (error) {
    console.error(`Failed to export file ${id}:`, error)
    return false
  }
}

export const exportMultipleData = async (
  api: AxiosInstance,
  ids: string[],
  format: SourceType,
  onProgress?: (progress: number) => void,
) => {
  const total = ids.length
  if (total === 0) return { success: 0, failed: 0 }

  try {
    let completed = 0
    const results = await Promise.all(
      ids.map(async (id) => {
        const success = await exportData(api, id, format)
        completed++
        onProgress?.(Math.round((completed / total) * 100))
        return { id, success }
      }),
    )

    const successCount = results.filter((r) => r.success).length
    const failureCount = total - successCount

    return { success: successCount, failed: failureCount }
  } catch (error) {
    console.error('Export error:', error)
    return { success: 0, failed: total }
  }
}
